import type { ServiceArea } from "../serviceAreas";

export const njAreas: ServiceArea[] = [
  {
    slug: "newark-nj",
    city: "Newark",
    state: "NJ",
    county: "Essex County",
    title: "Newark NJ Limo Service & EWR Airport Car Service",
    description:
      "Pre-booked limo and black car service in Newark, NJ. EWR pickups, rides to JFK, LGA and PHL, Prudential Center events and Cape Liberty. Call (929) 452-6031.",
    h1: "Limo Service in Newark, NJ",
    tagline:
      "Scheduled chauffeured rides around Newark Liberty, Penn Station and the city's arenas and theaters.",
    intro: [
      "Newark puts Newark Liberty International a few miles from downtown, with the NJ Turnpike, Route 21, I-280 and US-1/9 tying the city to the rest of the region. Our Newark work is mostly pre-booked: arriving executives met at EWR, hotel-to-office runs downtown, families heading to a concert at the Prudential Center and guests going to a show at NJPAC.",
      "GS Limo Elite is based in Glassboro, so we do not run a dispatch office in Newark. Instead, each trip is planned and confirmed ahead of time, with the chauffeur staged near your pickup before the scheduled time. That approach works well for flights, Newark Penn Station connections and Ironbound dinners where you want a confirmed ride waiting rather than a curbside gamble.",
    ],
    airports: [
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 10–20 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 35–55 min" },
      { code: "JFK", label: "JFK International", time: "About 45–65 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 75–95 min" },
    ],
    landmarks: [
      "Prudential Center",
      "Newark Penn Station",
      "New Jersey Performing Arts Center (NJPAC)",
      "Newark Broad Street Station",
      "Branch Brook Park",
      "Newark Museum of Art",
    ],
    neighborhoods: [
      "Ironbound",
      "Downtown Newark",
      "University Heights",
      "Forest Hill",
      "Vailsburg",
      "Harrison and Kearny border",
    ],
    popular: [
      {
        label: "Newark Liberty transfers",
        path: "/services/airport-transportation/ewr",
        blurb:
          "Meet-and-greet or curbside pickups at every EWR terminal, timed to your flight's actual arrival.",
      },
      {
        label: "Concerts and arena events",
        path: "/services/concerts",
        blurb:
          "Drop-off and return rides for Prudential Center and NJPAC shows, with a pickup spot agreed before the doors open.",
      },
      {
        label: "Cape Liberty cruise transfers",
        path: "/services/cruise-port",
        blurb:
          "Rides between Newark hotels or EWR and the Bayonne cruise terminal, with room for a full load of luggage.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive sedans and SUVs for downtown offices, Gateway Center meetings and client pickups.",
      },
    ],
    faqs: [
      {
        q: "Do you have a local office in Newark?",
        a: "No. We are based in Glassboro and serve Newark with pre-booked trips. Book ahead and we confirm the vehicle, chauffeur and pickup time before the day of travel.",
      },
      {
        q: "Can you meet me inside the terminal at Newark Liberty?",
        a: "Yes. Meet-and-greet service is available on request. The chauffeur tracks your flight, and any airport parking fees are confirmed in your quote.",
      },
      {
        q: "Can you pick up after a Devils game or a concert at the Prudential Center?",
        a: "Yes. Streets around the arena get congested after events, so we agree on a specific pickup point nearby and share it with you before the show.",
      },
      {
        q: "Do you drive from Newark to Manhattan or the New York airports?",
        a: "Yes. Trips to Manhattan, JFK and LaGuardia are common. Bridge and tunnel tolls depend on the route and are included in the confirmed quote.",
      },
    ],
    nearby: ["jersey-city-nj", "hoboken-nj", "new-york-city-ny", "new-brunswick-nj"],
  },
  {
    slug: "jersey-city-nj",
    city: "Jersey City",
    state: "NJ",
    county: "Hudson County",
    title: "Jersey City Limo & Car Service to EWR, JFK and LGA",
    description:
      "Chauffeured car service in Jersey City, NJ for EWR, JFK and LGA flights, Cape Liberty cruises, Exchange Place offices and nights out. Call (929) 452-6031.",
    h1: "Limo Service in Jersey City, NJ",
    tagline:
      "Pre-arranged rides from the waterfront towers to the airports, the cruise port and across the Hudson.",
    intro: [
      "Jersey City's waterfront at Exchange Place and Newport is packed with financial firms, hotels and high-rise apartments, and many residents rely on PATH for the daily commute. A chauffeured car fills the gaps PATH cannot: a 5 a.m. flight out of Newark Liberty, a family with luggage headed to the cruise terminal in Bayonne, or a group dinner in Manhattan through the Holland Tunnel.",
      "Because we are based in Glassboro, Jersey City rides are scheduled in advance rather than hailed on the spot. You tell us the pickup address, timing and passenger count, and we confirm the vehicle and route ahead of time, whether the trip runs down the Turnpike Extension to EWR or across town from Journal Square to Liberty State Park.",
    ],
    airports: [
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 15–25 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 30–50 min" },
      { code: "JFK", label: "JFK International", time: "About 45–65 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 85–100 min" },
    ],
    landmarks: [
      "Exchange Place and the Hudson River Waterfront Walkway",
      "Liberty State Park",
      "Liberty Science Center",
      "Newport Centre",
      "Journal Square Transportation Center",
      "Grove Street PATH plaza",
    ],
    neighborhoods: [
      "Downtown and Paulus Hook",
      "Newport",
      "The Heights",
      "Journal Square",
      "Greenville",
      "Bayonne border",
    ],
    popular: [
      {
        label: "Cape Liberty cruise transfers",
        path: "/services/cruise-port",
        blurb:
          "Short, luggage-friendly rides to the Bayonne cruise terminal, often combined with an EWR or JFK leg.",
      },
      {
        label: "JFK transfers",
        path: "/services/airport-transportation/jfk",
        blurb:
          "Scheduled rides to JFK with the bridge or tunnel route planned around the time of day.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Rides between Exchange Place offices, Newport hotels and Midtown or Lower Manhattan meetings.",
      },
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "A chauffeur for dinner downtown or a night across the river, with a set return time.",
      },
    ],
    faqs: [
      {
        q: "How far is Jersey City from the Cape Liberty cruise terminal?",
        a: "The Bayonne terminal is usually 15 to 25 minutes from downtown Jersey City by road. We plan the arrival around your ship's boarding window.",
      },
      {
        q: "Can you pick up at a high-rise building on the waterfront?",
        a: "Yes. Many waterfront buildings have a driveway or loading area, and we confirm the exact entrance with you so the chauffeur is not circling the block.",
      },
      {
        q: "Do you use the Holland Tunnel for trips into Manhattan?",
        a: "Usually, yes, though the chauffeur may choose the Lincoln Tunnel depending on your destination and traffic. Tolls are included in the confirmed quote.",
      },
      {
        q: "Is Jersey City service available on short notice?",
        a: "Our base is in South Jersey, so advance booking works best. Same-day requests are checked against availability before anything is confirmed.",
      },
    ],
    nearby: ["hoboken-nj", "newark-nj", "new-york-city-ny"],
  },
  {
    slug: "hoboken-nj",
    city: "Hoboken",
    state: "NJ",
    county: "Hudson County",
    title: "Hoboken Limo Service & Airport Car Service",
    description:
      "Pre-booked limo and car service in Hoboken, NJ. Rides to EWR, LGA and JFK, Hoboken Terminal pickups, weddings and Manhattan nights out. Call (929) 452-6031.",
    h1: "Limo Service in Hoboken, NJ",
    tagline:
      "Planned pickups on Hoboken's narrow, one-way streets, with routes to all three New York-area airports.",
    intro: [
      "Hoboken fits a lot into one square mile: Washington Street restaurants, waterfront parks along Sinatra Drive, Stevens Institute of Technology on Castle Point and Hoboken Terminal, where NJ Transit trains, PATH and NY Waterway ferries meet. Parking is tight and many streets are one-way, which is exactly why a chauffeured car with a planned pickup spot is easier than driving yourself.",
      "We run Hoboken trips by reservation from our Glassboro base. Common bookings include EWR departures, LaGuardia runs through the Lincoln Tunnel, Stevens family visits and wedding parties moving between a waterfront venue and a reception in Manhattan or elsewhere in Hudson County.",
    ],
    airports: [
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 20–30 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 25–45 min" },
      { code: "JFK", label: "JFK International", time: "About 45–70 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 90–105 min" },
    ],
    landmarks: [
      "Hoboken Terminal",
      "Stevens Institute of Technology",
      "Washington Street dining district",
      "Pier A Park and Frank Sinatra Drive",
      "Elysian Park",
      "Hoboken Historical Museum",
    ],
    neighborhoods: [
      "Downtown Hoboken",
      "Uptown Hoboken",
      "Northwest Hoboken",
      "Weehawken",
      "Union City",
      "Jersey City Heights",
    ],
    popular: [
      {
        label: "Newark Liberty transfers",
        path: "/services/airport-transportation/ewr",
        blurb:
          "Early departures and late arrivals at EWR without hunting for a cab on Washington Street.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Coordinated rides for couples and wedding parties between Hoboken, Hudson County and Manhattan venues.",
      },
      {
        label: "Bachelorette parties",
        path: "/services/bachelorette-party",
        blurb:
          "Group vehicles for a night of bar stops in Hoboken or across the river, with one agreed pickup point.",
      },
      {
        label: "School events",
        path: "/services/school-events",
        blurb:
          "Move-in, graduation and parents weekend rides for Stevens students and their families.",
      },
    ],
    faqs: [
      {
        q: "Where do you pick up in Hoboken when parking is tight?",
        a: "We agree on a legal spot near your address, often at a corner or a building entrance, and the chauffeur messages you when they arrive.",
      },
      {
        q: "Can you meet me at Hoboken Terminal?",
        a: "Yes. If you are arriving by train, PATH or ferry, we can meet you near the terminal and confirm the exact pickup spot in advance.",
      },
      {
        q: "Do you serve Stevens Institute of Technology?",
        a: "Yes. We handle airport trips and move-in or commencement rides for Stevens students and visiting families, booked ahead of time.",
      },
      {
        q: "Is LaGuardia or Newark closer to Hoboken?",
        a: "Newark Liberty is usually the shorter drive, but LaGuardia via the Lincoln Tunnel can be close outside rush hour. We can quote both.",
      },
    ],
    nearby: ["jersey-city-nj", "newark-nj", "new-york-city-ny"],
  },
  {
    slug: "new-brunswick-nj",
    city: "New Brunswick",
    state: "NJ",
    county: "Middlesex County",
    title: "New Brunswick Limo & Rutgers Car Service",
    description:
      "Chauffeured car service in New Brunswick, NJ for Rutgers events, State Theatre nights and transfers to EWR, PHL and JFK. Call (929) 452-6031.",
    h1: "Limo Service in New Brunswick, NJ",
    tagline:
      "Reserved rides for Rutgers families, hospital visitors and corporate guests along the Route 1 corridor.",
    intro: [
      "New Brunswick is anchored by Rutgers University, Robert Wood Johnson University Hospital and Johnson & Johnson's world headquarters, which brings a steady flow of visiting faculty, patients' families and business travelers. Route 1, Route 18 and NJ Turnpike Exit 9 put Newark Liberty within a reasonable drive, and the downtown theater district around Livingston Avenue draws evening crowds.",
      "Our chauffeurs come up from Glassboro for pre-booked New Brunswick trips, so we ask for your details ahead of time and confirm the plan in writing. Typical requests include Rutgers commencement weekends, SHI Stadium game days in Piscataway, State Theatre performances and early flights out of EWR.",
    ],
    airports: [
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 25–40 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 55–70 min" },
      { code: "JFK", label: "JFK International", time: "About 60–85 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 60–80 min" },
    ],
    landmarks: [
      "Rutgers University College Avenue Campus",
      "State Theatre New Jersey",
      "New Brunswick Performing Arts Center",
      "Robert Wood Johnson University Hospital",
      "New Brunswick train station",
      "SHI Stadium in Piscataway",
    ],
    neighborhoods: [
      "Downtown New Brunswick",
      "Highland Park",
      "Piscataway",
      "North Brunswick",
      "East Brunswick",
      "Somerset (Franklin Township)",
    ],
    popular: [
      {
        label: "School events",
        path: "/services/school-events",
        blurb:
          "Rides for Rutgers move-in, commencement and family visits across the College Avenue, Busch and Cook campuses.",
      },
      {
        label: "Sports events",
        path: "/services/sports-events",
        blurb:
          "Game-day drop-off and return for SHI Stadium and Jersey Mike's Arena, with a pickup point set before kickoff.",
      },
      {
        label: "Newark Liberty transfers",
        path: "/services/airport-transportation/ewr",
        blurb:
          "Turnpike rides to EWR for students, faculty and business visitors, planned around flight times.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive transportation for headquarters visits, hospital meetings and Route 1 office parks.",
      },
    ],
    faqs: [
      {
        q: "Can you pick up students from a Rutgers residence hall?",
        a: "Yes. Campus roads can be restricted on move-in and graduation days, so we confirm a reachable pickup point near the building before the trip.",
      },
      {
        q: "Do you provide rides to and from Robert Wood Johnson University Hospital?",
        a: "Yes, for scheduled visits and discharges. We are a car service, not medical transport, so passengers must be able to travel in a standard vehicle.",
      },
      {
        q: "How long is the ride from New Brunswick to Newark Liberty?",
        a: "Usually 25 to 40 minutes via the NJ Turnpike, longer in weekday rush hour. We build that margin into your pickup time.",
      },
      {
        q: "Do you need to book New Brunswick trips in advance?",
        a: "Yes, advance booking is recommended since our vehicles are dispatched from Glassboro. Busy Rutgers weekends fill up early.",
      },
    ],
    nearby: ["princeton-nj", "newark-nj", "trenton-nj", "jersey-city-nj"],
  },
  {
    slug: "princeton-nj",
    city: "Princeton",
    state: "NJ",
    county: "Mercer County",
    title: "Princeton NJ Limo & Executive Car Service",
    description:
      "Executive car and limo service in Princeton, NJ. Transfers to EWR, PHL and JFK, Princeton University visits, Route 1 offices and weddings. Call (929) 452-6031.",
    h1: "Limo Service in Princeton, NJ",
    tagline:
      "Discreet executive rides for the university, Route 1 offices and the trip to EWR or PHL.",
    intro: [
      "Princeton draws visiting academics, prospective students, parents and corporate travelers headed to offices along the Route 1 corridor in Plainsboro, West Windsor and Lawrence. The town sits roughly between Newark Liberty and Philadelphia International, so many clients choose whichever airport has the better flight and let the chauffeur handle the Turnpike or I-295.",
      "We serve Princeton by reservation from our Glassboro base, which keeps the drive manageable via I-295 and Route 1. Bookings range from Reunions weekend and commencement to McCarter Theatre evenings, weddings near Palmer Square and Amtrak or NJ Transit connections at Princeton Junction.",
    ],
    airports: [
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 40–55 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 50–65 min" },
      { code: "JFK", label: "JFK International", time: "About 75–95 min" },
      { code: "LGA", label: "LaGuardia (LGA)", time: "About 75–95 min" },
    ],
    landmarks: [
      "Princeton University and Nassau Hall",
      "Palmer Square",
      "McCarter Theatre Center",
      "Princeton Junction station",
      "Institute for Advanced Study",
      "Route 1 corporate corridor",
    ],
    neighborhoods: [
      "Downtown Princeton",
      "Western Section",
      "Riverside",
      "West Windsor",
      "Plainsboro",
      "Lawrence Township",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Rides to EWR, PHL, JFK or LaGuardia, with the airport choice and route discussed when you book.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Executive sedans for visiting speakers, board meetings and Route 1 office parks.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Transportation for couples and guests between Princeton chapels, inns and reception venues.",
      },
      {
        label: "School events",
        path: "/services/school-events",
        blurb:
          "Reunions, commencement and campus-visit rides for students, alumni and families.",
      },
    ],
    faqs: [
      {
        q: "Should I fly out of Newark or Philadelphia from Princeton?",
        a: "Both are under about an hour in normal traffic. We are happy to quote either so you can compare flights rather than drive times.",
      },
      {
        q: "Can you pick up at Princeton Junction station?",
        a: "Yes. We meet Amtrak and NJ Transit arrivals at Princeton Junction in West Windsor and confirm the pickup side of the station in advance.",
      },
      {
        q: "Do you handle Princeton Reunions and commencement weekends?",
        a: "Yes. These weekends book quickly and campus streets are partly closed, so we recommend reserving early and agreeing on a pickup point.",
      },
      {
        q: "Can a visiting speaker be picked up at the airport and driven to campus?",
        a: "Yes. Departments and companies often book a one-way or round-trip transfer, and the details can be confirmed with the organizer.",
      },
    ],
    nearby: ["trenton-nj", "new-brunswick-nj", "newark-nj", "philadelphia-pa"],
  },
  {
    slug: "trenton-nj",
    city: "Trenton",
    state: "NJ",
    county: "Mercer County",
    title: "Trenton NJ Car Service to PHL and EWR Airports",
    description:
      "Limo and black car service in Trenton, NJ. Rides to PHL and EWR, Trenton Transit Center pickups, State House meetings and events. Call (929) 452-6031.",
    h1: "Limo Service in Trenton, NJ",
    tagline:
      "Chauffeured rides for the state capital, its train station and the airports on both sides of the river.",
    intro: [
      "As New Jersey's capital, Trenton sees a steady stream of lobbyists, state employees and visitors headed to the State House and government offices on West State Street. Trenton Transit Center links Amtrak, NJ Transit and SEPTA, and US-1, I-295 and Route 29 connect the city to both Philadelphia International and Newark Liberty.",
      "From our base in Glassboro, Trenton is a straightforward drive up I-295. We schedule airport transfers, train station pickups, CURE Insurance Arena events and rides into Bucks County across the Delaware, all quoted and confirmed before the day of travel.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 40–55 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 45–60 min" },
      { code: "JFK", label: "JFK International", time: "About 80–100 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 70–85 min" },
    ],
    landmarks: [
      "New Jersey State House",
      "Trenton Transit Center",
      "CURE Insurance Arena",
      "Old Barracks Museum",
      "Trenton War Memorial",
      "Trenton Thunder Ballpark",
    ],
    neighborhoods: [
      "Downtown Trenton",
      "Mill Hill",
      "Chambersburg",
      "Hamilton Township",
      "Ewing Township",
      "Morrisville, PA",
    ],
    popular: [
      {
        label: "PHL transfers",
        path: "/services/airport-transportation/phl",
        blurb:
          "Rides to Philadelphia International via I-95 or I-295, with pickup times built around check-in.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Point-to-point service for State House meetings, agency visits and legal appointments.",
      },
      {
        label: "Sports events",
        path: "/services/sports-events",
        blurb:
          "Drop-off and return for games at the ballpark and events at CURE Insurance Arena.",
      },
      {
        label: "Hourly service",
        path: "/services/hourly-as-directed",
        blurb:
          "A chauffeur on call for back-to-back meetings around the capitol complex.",
      },
    ],
    faqs: [
      {
        q: "Can you meet me at Trenton Transit Center?",
        a: "Yes. We pick up Amtrak, NJ Transit and SEPTA passengers at the station and confirm which side of the building to meet on.",
      },
      {
        q: "Do you serve Trenton-Mercer Airport in Ewing?",
        a: "Yes, on request. Many Trenton travelers fly from PHL or EWR instead, and we can quote whichever airport suits your itinerary.",
      },
      {
        q: "Can you drive across the river into Pennsylvania?",
        a: "Yes. Morrisville, Yardley and other Bucks County towns are a short drive over the Delaware, and any bridge tolls are included in the quote.",
      },
      {
        q: "Do you offer hourly service for legislative or business days?",
        a: "Yes. Hourly service keeps a car and chauffeur available between meetings so you are not searching for parking downtown.",
      },
    ],
    nearby: ["princeton-nj", "bensalem-pa", "mount-laurel-nj", "new-brunswick-nj"],
  },
  {
    slug: "atlantic-city-nj",
    city: "Atlantic City",
    state: "NJ",
    county: "Atlantic County",
    title: "Atlantic City Limo Service & Casino Car Service",
    description:
      "Limo, party bus and car service in Atlantic City, NJ. ACY and PHL transfers, casino and Boardwalk Hall trips and bachelorette groups. Call (929) 452-6031.",
    h1: "Limo Service in Atlantic City, NJ",
    tagline:
      "Group rides to the casinos, the Boardwalk and the shore, plus transfers to ACY and PHL.",
    intro: [
      "Atlantic City is one of our most requested destinations for celebrations. Bachelor and bachelorette weekends, birthday trips, concerts at Boardwalk Hall and conventions at the Atlantic City Convention Center all involve groups who would rather share one vehicle than coordinate several cars and casino garages.",
      "The Atlantic City Expressway runs from our Glassboro area straight to the shore, so trips here are part of our regular South Jersey routine. We pick up in Atlantic City for flights from Atlantic City International in Egg Harbor Township or PHL, and we bring groups down from Philadelphia, Cherry Hill and North Jersey for a night at the Borgata, Hard Rock or Ocean.",
    ],
    airports: [
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 15–25 min" },
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 60–75 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 110–130 min" },
      { code: "JFK", label: "JFK International", time: "About 140–165 min" },
    ],
    landmarks: [
      "Jim Whelan Boardwalk Hall",
      "Borgata Hotel Casino & Spa",
      "Hard Rock Hotel & Casino Atlantic City",
      "Ocean Casino Resort",
      "Atlantic City Convention Center",
      "Tanger Outlets The Walk",
    ],
    neighborhoods: [
      "Marina District",
      "Chelsea",
      "Ventnor City",
      "Margate City",
      "Brigantine",
      "Egg Harbor Township",
    ],
    popular: [
      {
        label: "Bachelorette parties",
        path: "/services/bachelorette-party",
        blurb:
          "Sprinter vans and party bus requests for weekend groups moving between hotels, dinner and clubs.",
      },
      {
        label: "Party bus",
        path: "/services/party-bus",
        blurb:
          "Larger group trips to Atlantic City from Philadelphia, South Jersey and North Jersey.",
      },
      {
        label: "ACY transfers",
        path: "/services/airport-transportation/acy",
        blurb:
          "Quick rides between Atlantic City International and the casino hotels or shore towns.",
      },
      {
        label: "Concerts",
        path: "/services/concerts",
        blurb:
          "Rides to Boardwalk Hall and casino theaters, with a confirmed meeting point after the show.",
      },
    ],
    faqs: [
      {
        q: "Can you bring our group to Atlantic City and back the same night?",
        a: "Yes. Round-trip evenings are common. We agree on the return time up front, and any wait time or extensions are confirmed in the quote.",
      },
      {
        q: "Where do you pick up at the casinos?",
        a: "Most casino hotels have a dedicated limo or valet area. We confirm the right entrance for your property before the trip.",
      },
      {
        q: "How far is ACY from the casinos?",
        a: "Atlantic City International is in Egg Harbor Township, usually 15 to 25 minutes from the Boardwalk and Marina District hotels.",
      },
      {
        q: "Can you handle large bachelor or bachelorette groups?",
        a: "Yes. Sprinter vans suit most groups, and we can look into party bus availability for larger parties. Share your headcount early so we can confirm.",
      },
    ],
    nearby: ["vineland-nj", "glassboro-nj", "sicklerville-winslow-nj", "philadelphia-pa"],
  },
  {
    slug: "mount-laurel-nj",
    city: "Mount Laurel",
    state: "NJ",
    county: "Burlington County",
    title: "Mount Laurel NJ Limo & Corporate Car Service",
    description:
      "Limo and corporate car service in Mount Laurel, NJ. PHL, EWR and JFK transfers, Route 73 hotel pickups, business travel and weddings. Call (929) 452-6031.",
    h1: "Limo Service in Mount Laurel, NJ",
    tagline:
      "Business-focused car service for the Route 73 and Turnpike Exit 4 hotel and office corridor.",
    intro: [
      "Mount Laurel sits at NJ Turnpike Exit 4 and I-295, with Route 73 and Route 38 lined with hotels, office parks and corporate campuses. That location makes it a natural base for business travelers visiting South Jersey, and a convenient starting point for trips to Philadelphia International or north on the Turnpike to Newark Liberty.",
      "Mount Laurel is a short drive from our Glassboro base, so we cover it regularly for airport runs, client pickups at Fellowship Road and Centerton Road offices, and evening events. Residents in Ramblewood, Larchmont and Rancocas Woods also book us for weddings, proms and nights out in Philadelphia.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 25–35 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 55–70 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 55–65 min" },
      { code: "JFK", label: "JFK International", time: "About 85–105 min" },
    ],
    landmarks: [
      "NJ Turnpike Exit 4 and Route 73",
      "Route 73 hotel corridor",
      "Fellowship Road office parks",
      "Laurel Acres Park",
      "Rancocas Woods Village of Shops",
      "Larchmont Commons",
    ],
    neighborhoods: [
      "Ramblewood",
      "Larchmont",
      "Rancocas Woods",
      "Moorestown border",
      "Hainesport",
      "Marlton border",
    ],
    popular: [
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Hotel-to-office rides, client pickups and roadshows across the Route 73 business district.",
      },
      {
        label: "PHL transfers",
        path: "/services/airport-transportation/phl",
        blurb:
          "Rides to Philadelphia International in about half an hour, timed around your terminal.",
      },
      {
        label: "Newark Liberty transfers",
        path: "/services/airport-transportation/ewr",
        blurb:
          "Straight up the Turnpike from Exit 4 for flights out of EWR.",
      },
      {
        label: "Prom",
        path: "/services/prom",
        blurb:
          "Group rides for Lenape and other local high school proms, with set pickup and return times.",
      },
    ],
    faqs: [
      {
        q: "Can you pick up guests from Mount Laurel hotels?",
        a: "Yes. We pick up at hotels along Route 73 and Fellowship Road and can meet guests in the lobby if you request it.",
      },
      {
        q: "How long is the ride from Mount Laurel to PHL?",
        a: "Typically 25 to 35 minutes using I-295 and the Walt Whitman or Ben Franklin Bridge, depending on traffic and time of day.",
      },
      {
        q: "Do you set up recurring rides for Mount Laurel companies?",
        a: "Yes. We can arrange regular airport runs and client transportation for local offices, with each trip confirmed in advance.",
      },
      {
        q: "Is Newark Liberty practical from Mount Laurel?",
        a: "Yes. Turnpike Exit 4 is right in town, so EWR is usually about an hour away. Turnpike tolls are included in the quote.",
      },
    ],
    nearby: ["moorestown-nj", "marlton-nj", "cherry-hill-nj", "philadelphia-pa"],
  },
  {
    slug: "moorestown-nj",
    city: "Moorestown",
    state: "NJ",
    county: "Burlington County",
    title: "Moorestown Limo & Airport Car Service",
    description:
      "Chauffeured car service in Moorestown, NJ. Airport rides to PHL, EWR and JFK, Main Street pickups, weddings and corporate travel. Call (929) 452-6031.",
    h1: "Limo Service in Moorestown, NJ",
    tagline:
      "Polished rides from Main Street and Moorestown's tree-lined neighborhoods to the airport or the city.",
    intro: [
      "Moorestown combines a walkable Main Street with quiet residential streets and a large employer base along Route 38, including the Lockheed Martin campus. Residents often book us for PHL flights and Philadelphia evenings, while companies use our sedans and SUVs for visitors arriving from the airport.",
      "Moorestown is an easy drive from our Glassboro base via I-295, so local pickups are part of our normal South Jersey schedule. We also handle wedding days at area churches and country clubs, Moorestown High School proms and family trips to the Jersey Shore.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 25–35 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 55–70 min" },
      { code: "JFK", label: "JFK International", time: "About 85–105 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 60–70 min" },
    ],
    landmarks: [
      "Main Street shopping district",
      "Moorestown Mall",
      "Strawbridge Lake Park",
      "Perkins Center for the Arts",
      "Lockheed Martin campus off Route 38",
      "Smith-Cadbury Mansion",
    ],
    neighborhoods: [
      "Moorestown center",
      "Lenola",
      "Maple Shade",
      "Cinnaminson",
      "Mount Laurel border",
      "Cherry Hill border",
    ],
    popular: [
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "Rides to PHL, EWR, JFK and ACY with flight tracking and a pickup time agreed in advance.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Ceremony and reception transportation for Moorestown churches, clubs and nearby venues.",
      },
      {
        label: "Corporate travel",
        path: "/services/corporate-travel",
        blurb:
          "Visitor pickups and meeting rides for offices along Route 38 and the Fellowship Road area.",
      },
      {
        label: "Birthdays",
        path: "/services/birthdays",
        blurb:
          "A chauffeured evening for a milestone birthday dinner in Moorestown or Philadelphia.",
      },
    ],
    faqs: [
      {
        q: "Do you serve Lenola and the rest of Moorestown Township?",
        a: "Yes. Lenola, the Main Street area and all Moorestown neighborhoods are part of our regular service area.",
      },
      {
        q: "Can you pick up visitors arriving at PHL for Moorestown businesses?",
        a: "Yes. We track the flight, meet the guest and drive them to the office or hotel, with billing details confirmed in advance.",
      },
      {
        q: "Can you provide cars for a wedding at a Moorestown venue?",
        a: "Yes. Tell us the ceremony and reception locations and the size of the party, and we will suggest vehicles and a timeline.",
      },
      {
        q: "How far ahead should I book a Moorestown airport ride?",
        a: "A few days ahead is ideal, and earlier around holidays. Same-day requests are possible when a vehicle is free.",
      },
    ],
    nearby: ["mount-laurel-nj", "cherry-hill-nj", "haddonfield-nj", "marlton-nj"],
  },
  {
    slug: "marlton-nj",
    city: "Marlton",
    state: "NJ",
    county: "Burlington County",
    title: "Marlton (Evesham) Limo & Car Service",
    description:
      "Limo and car service in Marlton and Evesham Township, NJ. Transfers to PHL, ACY and EWR, weddings, proms and shore trips via Route 73. Call (929) 452-6031.",
    h1: "Limo Service in Marlton, NJ",
    tagline:
      "Chauffeured rides for Evesham Township families and businesses along Route 70 and Route 73.",
    intro: [
      "Marlton is the main community in Evesham Township, built around the crossing of Route 70 and Route 73. That junction gives quick access to I-295 and the Turnpike for airport runs, and Route 73 heads south toward the Atlantic City Expressway for shore weekends and casino nights.",
      "We reach Marlton easily from our Glassboro base and cover Kings Grant, Marlton Lakes and the rest of the township. Popular bookings include Cherokee High School proms, weddings at golf and country club venues, PHL departures and birthday groups heading to Atlantic City.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 25–40 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 50–60 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 60–75 min" },
      { code: "JFK", label: "JFK International", time: "About 90–110 min" },
    ],
    landmarks: [
      "The Promenade at Sagemore",
      "Route 70 and Route 73 interchange",
      "Marlton Crossing shopping center",
      "Indian Spring Country Club",
      "Main Street in old Marlton",
      "Evesham Township municipal complex",
    ],
    neighborhoods: [
      "Kings Grant",
      "Marlton Lakes",
      "Old Marlton village",
      "Medford border",
      "Mount Laurel border",
      "Cherry Hill border",
    ],
    popular: [
      {
        label: "Prom",
        path: "/services/prom",
        blurb:
          "Group transportation for Cherokee and other area proms, with parent-approved pickup and drop-off times.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Rides for couples and wedding parties at country club and banquet venues around Evesham.",
      },
      {
        label: "ACY transfers",
        path: "/services/airport-transportation/acy",
        blurb:
          "Direct rides down Route 73 and the Expressway for flights out of Atlantic City International.",
      },
      {
        label: "Airport transfers",
        path: "/airports",
        blurb:
          "PHL, EWR and JFK rides with luggage room and pickup times planned around your flight.",
      },
    ],
    faqs: [
      {
        q: "Do you cover all of Evesham Township, not just Marlton?",
        a: "Yes. Kings Grant, Marlton Lakes and every other part of Evesham Township are within our regular service area.",
      },
      {
        q: "Can you take our group from Marlton to Atlantic City?",
        a: "Yes. It is usually about an hour via Route 73 and the Atlantic City Expressway. Tolls and wait time are confirmed in your quote.",
      },
      {
        q: "Do you offer prom transportation for Cherokee High School?",
        a: "Yes. We coordinate with students and parents on the pickup location, venue and return time, and confirm everything before prom night.",
      },
      {
        q: "Which airport is fastest from Marlton?",
        a: "Philadelphia International is usually closest, at roughly 25 to 40 minutes. ACY is a good option if your airline flies from there.",
      },
    ],
    nearby: ["mount-laurel-nj", "cherry-hill-nj", "voorhees-nj", "moorestown-nj"],
  },
  {
    slug: "haddonfield-nj",
    city: "Haddonfield",
    state: "NJ",
    county: "Camden County",
    title: "Haddonfield NJ Limo Service & PHL Car Service",
    description:
      "Limo and black car service in Haddonfield, NJ. PHL, EWR and ACY transfers, Kings Highway pickups, weddings and Philadelphia nights out. Call (929) 452-6031.",
    h1: "Limo Service in Haddonfield, NJ",
    tagline:
      "Chauffeured rides from Haddonfield's historic downtown to the airport, the city and the shore.",
    intro: [
      "Haddonfield is known for its historic downtown along Kings Highway, with independent shops, restaurants and colonial-era buildings like the Indian King Tavern. The borough is close to I-295, Route 70 and the PATCO line, and Philadelphia International is usually less than half an hour away.",
      "Haddonfield is around 25 minutes from our Glassboro base, so we serve it often. Residents book us for early PHL flights, wedding days, dinners and shows in Center City, and Atlantic City weekends, while local businesses use us for client pickups and visitors arriving by air.",
    ],
    airports: [
      { code: "PHL", label: "Philadelphia International (PHL)", time: "About 20–30 min" },
      { code: "ACY", label: "Atlantic City International (ACY)", time: "About 55–65 min" },
      { code: "EWR", label: "Newark Liberty (EWR)", time: "About 65–80 min" },
      { code: "JFK", label: "JFK International", time: "About 90–110 min" },
    ],
    landmarks: [
      "Kings Highway downtown district",
      "Indian King Tavern Museum",
      "Haddonfield PATCO station",
      "Hadrosaurus Park",
      "Greenfield Hall",
      "Tavistock Country Club",
    ],
    neighborhoods: [
      "Downtown Haddonfield",
      "Haddon Township (Westmont)",
      "Collingswood",
      "Haddon Heights",
      "Barrington",
      "Cherry Hill border",
    ],
    popular: [
      {
        label: "PHL transfers",
        path: "/services/airport-transportation/phl",
        blurb:
          "Quick rides to Philadelphia International, often under half an hour from your front door.",
      },
      {
        label: "Weddings",
        path: "/services/weddings",
        blurb:
          "Transportation for couples and guests between Haddonfield churches and South Jersey or Philadelphia receptions.",
      },
      {
        label: "Night on the town",
        path: "/services/night-on-the-town",
        blurb:
          "A chauffeured evening in Center City or along Kings Highway, without worrying about parking.",
      },
      {
        label: "Wine and sightseeing",
        path: "/services/wine-and-sightseeing",
        blurb:
          "Hourly service for a day at South Jersey wineries or a tour around the region.",
      },
    ],
    faqs: [
      {
        q: "Can you pick up on Kings Highway in downtown Haddonfield?",
        a: "Yes. Downtown parking is limited, so we confirm a convenient curbside spot or side street near your restaurant or shop.",
      },
      {
        q: "How long is the drive from Haddonfield to PHL?",
        a: "Usually 20 to 30 minutes via I-295 or the Walt Whitman Bridge, depending on traffic.",
      },
      {
        q: "Do you serve neighboring towns like Collingswood and Haddon Township?",
        a: "Yes. Collingswood, Westmont, Haddon Heights and nearby Camden County towns are all part of our regular service area.",
      },
      {
        q: "Can you drive us into Philadelphia for dinner and a show?",
        a: "Yes. We can do a drop-off and return at a set time, or keep the car with you on an hourly basis. Bridge tolls are included in the quote.",
      },
    ],
    nearby: ["cherry-hill-nj", "voorhees-nj", "moorestown-nj", "philadelphia-pa"],
  },
];
